import { useEffect, useState } from 'react';
import { useMayWrite, useOrg } from '../api/app-context';
import { useCredits } from '../api/hooks';
import { creditStanding, type CreditStanding } from '../lib/credit-standing';
import { CREDITS_LOW_LABEL, CREDITS_OUT_LABEL, creditsLowLine, creditsOutLine } from '../lib/copy';
import { Button } from './ui/button';
import './credit-notice.css';

const dismissKey = (orgId: string) => `ni.credits-low-dismissed:${orgId}`;

function readDismissed(orgId: string): boolean {
  try {
    return window.localStorage.getItem(dismissKey(orgId)) !== null;
  } catch {
    return false;
  }
}

function writeDismissed(orgId: string, dismissed: boolean): void {
  try {
    if (dismissed) window.localStorage.setItem(dismissKey(orgId), '1');
    else window.localStorage.removeItem(dismissKey(orgId));
  } catch {
    /* Storage refused: the line simply comes back on the next load. */
  }
}

/**
 * **Credit running low or out, before a press is refused** (ruled 2026-09-26):
 * one slim line at the top of every page, for owners only, the one level that
 * can press priced work. It is not the AI allowance banner and never borrows its
 * words.
 *
 * Low can be dismissed, and stays dismissed until the standing changes: it runs
 * out, or credit is added and it later drops low again. Out cannot be, for the
 * reason the allowance banner cannot: a dismissed line leaves someone meeting a
 * refusal with no memory of why.
 */
export function CreditNotice() {
  const org = useOrg();
  const owner = useMayWrite('all');
  const credits = useCredits();
  const standing: CreditStanding = credits.data ? creditStanding(credits.data) : { state: 'none' };
  const [dismissed, setDismissed] = useState(() => readDismissed(org.id));

  useEffect(() => setDismissed(readDismissed(org.id)), [org.id]);
  /* Only a standing seen to be something other than low forgets the dismissal;
     a read still loading or failed does not. */
  const known = credits.data !== undefined;
  useEffect(() => {
    if (known && standing.state !== 'low' && dismissed) {
      writeDismissed(org.id, false);
      setDismissed(false);
    }
  }, [known, standing.state, dismissed, org.id]);

  if (!owner) return null;
  return (
    <CreditNoticeView
      standing={standing}
      dismissed={dismissed}
      onDismiss={() => {
        writeDismissed(org.id, true);
        setDismissed(true);
      }}
    />
  );
}

export function CreditNoticeView({
  standing,
  dismissed,
  onDismiss,
}: {
  standing: CreditStanding;
  dismissed: boolean;
  onDismiss: () => void;
}) {
  if (standing.state === 'out')
    return (
      <aside className="demo-notice credit-notice" role="note">
        <strong>{CREDITS_OUT_LABEL}</strong>
        <span>{creditsOutLine(standing.quickLooksLeft)}</span>
      </aside>
    );
  if (standing.state === 'low' && !dismissed)
    return (
      <aside className="demo-notice credit-notice" role="note">
        <strong>{CREDITS_LOW_LABEL}</strong>
        <span>{creditsLowLine(standing.available, standing.dossier, standing.unitsPerCredit)}</span>
        <Button variant="act" tiny onClick={onDismiss}>
          Dismiss
        </Button>
      </aside>
    );
  return null;
}

/** The same standing, as a line in Settings' credits panel (no dismiss). */
export function CreditStandingLine({ standing }: { standing: CreditStanding }) {
  if (standing.state === 'out')
    return (
      <p>
        <strong>{CREDITS_OUT_LABEL}.</strong> {creditsOutLine(standing.quickLooksLeft)}
      </p>
    );
  if (standing.state === 'low')
    return (
      <p>
        <strong>{CREDITS_LOW_LABEL}.</strong>{' '}
        {creditsLowLine(standing.available, standing.dossier, standing.unitsPerCredit)}
      </p>
    );
  return null;
}
